/*!
 * asset-chain.js — «свой origin первым, зеркало запасным, падение — в телеметрию».
 *
 * ЗАЧЕМ
 * -----
 * Общий помощник cdn() выбирает адрес ПО ЛОКАЛИ: ru идёт к нам, остальные — на
 * внешний CDN. Для картинки это нормально, для скрипта, которым тул РАБОТАЕТ, —
 * нет, и по двум причинам сразу:
 *
 *   1. Локаль ≠ страна. Человек из России, открывший английскую версию,
 *      уезжает на jsdelivr/unpkg. Если внешний хост недоступен, тул не
 *      «деградирует» — он просто мёртв.
 *   2. Обратное тоже верно: intl-origin, зависящий от чужого CDN, не является
 *      независимым. Каждый origin должен уметь обслужить свой трафик сам.
 *
 * Поэтому здесь порядок ВСЕГДА: наш origin → внешнее зеркало. И если сработало
 * зеркало, мы об этом узнаём — молча хромающий тул ничему не учит.
 *
 * ⚠️ РЯДОМ ЖИВЁТ БЛИЗНЕЦ: public/js/midi-humanizer/assets.js (loadScriptChain).
 * Он ESM и виден только внутри графа модулей своего тула, поэтому обычному
 * скрипту недоступен — отсюда этот файл. Политика у них ОДНА и та же; если
 * будете сводить, сводите осознанно, а не потому что похожи.
 *
 * API (глобал window.__assetChain):
 *   importModule(urls, label) -> Promise<Module>   динамический import по цепочке
 *   loadScript(urls, label)   -> Promise<string>   обычный <script>
 *   loadStyle(urls, label)    -> Promise<string>   <link rel=stylesheet>
 *   pickUrl(urls, label)      -> Promise<string>   первый ответивший на HEAD/GET
 *
 * `urls` — массив по приоритету; null/undefined отбрасываются. `label` попадает
 * в телеметрию, поэтому называйте ресурс, а не файл: 'three', 'leaflet'.
 */
(function (root) {
  'use strict';

  var cache = Object.create(null);   // url -> Promise

  function report(event, data) {
    try { root.__ae && root.__ae(event, data || {}); } catch (e) { /* телеметрия не ломает тул */ }
  }

  /* ── Ни одна попытка не ждёт вечно (2026-09-30) ──────────────────────────
     Тег, который не загрузился и не упал, раньше вешал тул навсегда: ни
     onload, ни onerror — и промис висит. Так /stamp-maker (у него была своя
     копия этого загрузчика) держал оплатившую Премиум покупательницу 173 с
     в «exporting» на каждом из шести заходов, кнопка мертва во всех
     форматах (возврат #37); у /business-card-maker те же уходы из стадии
     «exporting» через 1–11 минут. Попытка, молчащая ATTEMPT_MS ВИДИМОГО
     времени (__visibleTimeout: скрытая вкладка — не зависание), считается
     неудачной: дальше следующий адрес цепочки. Срок щедрый — он ловит
     тишину, а не медленную сеть (самая тяжёлая библиотека здесь ≈ 1 МБ
     сжатой). Опоздавший ответ брошенной попытки безвреден: UMD-библиотека
     переписывает свой глобал тем же объектом. */
  var ATTEMPT_MS = 45000;

  function afterVisible(fn, ms) {
    if (typeof root.__visibleTimeout === 'function') return root.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function () { clearTimeout(h); };
  }

  function bounded(p, ms, url) {
    var cancel = null;
    var timer = new Promise(function (_, reject) {
      cancel = afterVisible(function () { var e = new Error('stalled:' + url); e.stalled = true; reject(e); }, ms);
    });
    return Promise.race([p, timer]).then(
      function (v) { if (cancel) cancel(); return v; },
      function (e) { if (cancel) cancel(); throw e; }
    );
  }

  function clean(urls) {
    var seen = Object.create(null), out = [];
    (urls || []).forEach(function (u) {
      if (!u || seen[u]) return;
      seen[u] = 1;
      out.push(u);
    });
    return out;
  }

  /**
   * Пройти список по порядку. Первый успех выигрывает; если выиграл не первый —
   * сообщаем, КАКОЙ по счёту и что это был за ресурс.
   */
  function chain(list, label, attempt, retrySame) {
    var urls = clean(list);
    if (!urls.length) return Promise.reject(new Error('no_url:' + label));
    // Тегу с одним адресом нечем подстраховаться — вторая попытка идёт на тот
    // же адрес с другим query: тег на адрес, который ещё грузится, может просто
    // присоединиться к зависшему запросу. Только для <script>/<link>: у import()
    // другой query — это второй экземпляр модуля.
    if (retrySame && urls.length === 1) urls.push(urls[0] + (urls[0].indexOf('?') >= 0 ? '&' : '?') + 'retry=1');

    var i = 0;
    function next(lastErr) {
      if (i >= urls.length) {
        report('tool_diagnostic', { issue: 'asset_unreachable', asset: label, tried: urls.length });
        return Promise.reject(lastErr || new Error('all_failed:' + label));
      }
      var url = urls[i++];
      return bounded(attempt(url), ATTEMPT_MS, url).then(function (res) {
        if (i > 1) {
          // Наш origin не отдал — это событие, а не мелочь: так мы узнаём про
          // блокировки и про файл, забытый на одном из origin'ов.
          report('asset_fallback', { asset: label, index: i - 1, url: url });
        }
        return res;
      }, function (e) {
        if (e && e.stalled) {
          // зависшая попытка не должна достаться следующему вызову из кэша
          delete cache[url];
          // тишина видна только без согласия на аналитику — канал tier-0
          try { root.__aeCritical && root.__aeCritical('asset_stalled', { asset: label, index: i - 1, url: String(url).slice(0, 160) }); } catch (x) {}
        }
        return next(e);
      });
    }
    return next(null);
  }

  function once(url, make) {
    var p = cache[url];
    if (p) return p;
    p = make(url).catch(function (e) { delete cache[url]; throw e; });
    cache[url] = p;
    return p;
  }

  function importModule(urls, label) {
    return chain(urls, label || 'module', function (url) {
      return once(url, function (u) { return import(/* webpackIgnore: true */ u); });
    });
  }

  function loadScript(urls, label) {
    return chain(urls, label || 'script', function (url) {
      return once(url, function (u) {
        return new Promise(function (resolve, reject) {
          var s = document.createElement('script');
          s.src = u;
          s.async = true;
          s.onload = function () { resolve(u); };
          s.onerror = function () { reject(new Error('script_failed:' + u)); };
          document.head.appendChild(s);
        });
      });
    }, true);
  }

  function loadStyle(urls, label) {
    return chain(urls, label || 'style', function (url) {
      return once(url, function (u) {
        return new Promise(function (resolve, reject) {
          var l = document.createElement('link');
          l.rel = 'stylesheet';
          l.href = u;
          l.onload = function () { resolve(u); };
          l.onerror = function () { reject(new Error('style_failed:' + u)); };
          document.head.appendChild(l);
        });
      });
    }, true);
  }

  /** Первый адрес, который реально отвечает. Для картинок/плиток. */
  function pickUrl(urls, label) {
    return chain(urls, label || 'url', function (url) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.onload = function () { resolve(url); };
        img.onerror = function () { reject(new Error('probe_failed:' + url)); };
        img.src = url;
      });
    });
  }

  root.__assetChain = {
    importModule: importModule,
    loadScript: loadScript,
    loadStyle: loadStyle,
    pickUrl: pickUrl,
  };
}(window));
